// Meta Ad Builder — schedule wall-clock + timezone resolution.
//
// Campaign / ad-set start+end are stored as WALL-CLOCK local datetimes
// ('YYYY-MM-DDTHH:mm', no offset) — what the AM typed, timezone-agnostic. The
// absolute instant is resolved ONCE at the boundaries (publish serialization +
// the future/order validation checks) against an IANA timezone:
//
//   - DEFAULT: the AD ACCOUNT's timezone (Meta runs the campaign in account tz, so
//     "5am" means 5am account-local). Same tz source the city-search scope uses
//     (the ad-account timezoneName from listAdAccounts → useLinkedAdAccount).
//   - OVERRIDE (campaign.scheduleUseAustralianEastern): Australian Eastern time,
//     for the rare misconfigured-account case. Resolved via 'Australia/Sydney' so
//     the offset is DST-correct — AEST (+10) in winter, AEDT (+11) during daylight
//     saving — never a hardcoded offset.
//
// PURE: no I/O, no ambient Date (callers inject `now`). Uses Intl for the IANA
// offset lookup (DST-aware), so a wall-clock resolves to the correct instant for
// its own date.

// Australian Eastern (NSW/VIC observe DST; the IANA zone carries the AEST/AEDT
// transition table — do NOT substitute a fixed +10/+11).
export const AUSTRALIAN_EASTERN_TZ = 'Australia/Sydney';

// Default time-of-day applied when a schedule DATE is first picked (editable).
export const DEFAULT_START_TIME = '05:00'; // 5:00am
export const DEFAULT_END_TIME = '22:00'; // 10:00pm

// The timezone a campaign's wall-clock schedule resolves against.
export function effectiveScheduleTimeZone(
  useAustralianEastern: boolean,
  accountTimeZone: string | null,
): string | null {
  return useAustralianEastern ? AUSTRALIAN_EASTERN_TZ : accountTimeZone;
}

// Parse the wall-clock portion of a value. Tolerant of trailing seconds / millis /
// 'Z' / offset (legacy full-ISO values) — only the Y-M-D H:m are read. null if the
// value isn't a recognisable datetime.
function parseWallClock(value: string): { y: number; mo: number; d: number; h: number; mi: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value.trim());
  if (!m) return null;
  return { y: +m[1]!, mo: +m[2]!, d: +m[3]!, h: +m[4]!, mi: +m[5]! };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// The IANA zone's UTC offset (minutes, + = east of UTC) at a given absolute
// instant. Formats the instant in the zone, reinterprets those wall-clock parts as
// UTC, and diffs — the standard Intl offset trick.
//
// ⚠️ EXPORTED (2026-09-09) so `server/modules/crmSync/timezone.ts` can compare
// two zones' offsets WITHOUT writing a second implementation of this. That
// module decides which day a GymSales lead lands on, against data that carries
// no event time to check it against, so a duplicate — one branch out, or one
// DST-transition case short — would be an hour wrong with nothing to catch it.
// Exporting a tested primitive is the cheap way to make a duplicate unnecessary.
//
// ⚠️ THROWS on a zone `Intl` does not recognise (RangeError). Callers validate
// first; crmSync's `isKnownIanaZone` is that check.
export function tzOffsetMinutesAt(instantMs: number, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(instantMs))) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  const asUTC = Date.UTC(+map.year!, +map.month! - 1, +map.day!, +map.hour!, +map.minute!, +map.second!);
  return Math.round((asUTC - instantMs) / 60000);
}

// Resolve a wall-clock local datetime in `timeZone` to its absolute instant (ms).
// DST-correct: re-checks the offset at the candidate instant (handles the case
// where the naive guess lands on the other side of a transition). A null timeZone
// falls back to interpreting the wall-clock as UTC (deterministic; only hit when
// the account tz is unknown and no override is set). null if value isn't a datetime.
export function wallClockToInstantMs(value: string, timeZone: string | null): number | null {
  const w = parseWallClock(value);
  if (!w) return null;
  const asUTC = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, 0);
  if (!timeZone) return asUTC;
  const off1 = tzOffsetMinutesAt(asUTC, timeZone);
  let instant = asUTC - off1 * 60000;
  const off2 = tzOffsetMinutesAt(instant, timeZone);
  if (off2 !== off1) instant = asUTC - off2 * 60000;
  return instant;
}

function formatOffset(minutes: number): string {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  return `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

// Resolve a wall-clock local datetime to an offset-bearing ISO-8601 string Meta
// accepts (e.g. '2026-07-01T05:00:00+10:00'). The wall-clock is preserved verbatim;
// only the trailing offset is computed for the value's own date (so a winter date
// gets +10:00 and a summer date +11:00 for Australian Eastern). A null timeZone
// emits 'Z' (UTC interpretation). null/blank value → null; an unrecognised value
// passes through untouched (defensive — shouldn't happen for a real schedule).
export function wallClockToOffsetIso(value: string | null, timeZone: string | null): string | null {
  if (!value) return null;
  const w = parseWallClock(value);
  if (!w) return value;
  const offset = timeZone
    ? formatOffset(tzOffsetMinutesAt(wallClockToInstantMs(value, timeZone)!, timeZone))
    : 'Z';
  return `${String(w.y).padStart(4, '0')}-${pad2(w.mo)}-${pad2(w.d)}T${pad2(w.h)}:${pad2(w.mi)}:00${offset}`;
}

// Convert an absolute instant (ISO-8601 carrying an offset / 'Z', or any value
// Date.parse understands) to the WALL-CLOCK 'YYYY-MM-DDTHH:mm' in `timeZone`. The
// INVERSE of wallClockToInstantMs — used to seed the builder schedule from an
// absolute timestamp (e.g. a rollout's UTC start/end) so the seeded wall-clock both
// DISPLAYS and RESOLVES in the SAME tz the builder uses (the ad account's). DST-
// correct via Intl (a June instant reads AEST, a January one AEDT for Sydney). A
// null timeZone falls back to the UTC wall-clock (deterministic; only hit when the
// ad-account tz is unknown). null/blank/unparseable value → null.
export function instantIsoToWallClock(
  value: string | null | undefined,
  timeZone: string | null,
): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  if (!timeZone) {
    const d = new Date(ms);
    return `${String(d.getUTCFullYear()).padStart(4, '0')}-${pad2(d.getUTCMonth() + 1)}-${pad2(
      d.getUTCDate(),
    )}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
  }
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(ms))) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  // hourCycle h23 can emit '24' for midnight in some engines — normalise to '00'.
  const hour = map.hour === '24' ? '00' : map.hour!;
  return `${map.year}-${map.month}-${map.day}T${hour}:${map.minute}`;
}

// ---------- UI helpers (split / combine the date + time inputs) ----------

export function wallClockDatePart(value: string | null): string {
  const w = value ? parseWallClock(value) : null;
  return w ? `${String(w.y).padStart(4, '0')}-${pad2(w.mo)}-${pad2(w.d)}` : '';
}

export function wallClockTimePart(value: string | null): string {
  const w = value ? parseWallClock(value) : null;
  return w ? `${pad2(w.h)}:${pad2(w.mi)}` : '';
}

// Combine a date input ('YYYY-MM-DD') + time input ('HH:mm') into a stored
// wall-clock value. Empty date → null (no schedule). Empty time → defaultTime (so
// picking a date alone yields the 5am/10pm default).
export function combineDateTime(date: string, time: string, defaultTime: string): string | null {
  if (!date) return null;
  const t = /^\d{2}:\d{2}$/.test(time) ? time : defaultTime;
  return `${date}T${t}`;
}
